// @erp/module-audit/fe · queryOptions accessor-factories (DEV-GUIDE-002 §3.2).
// Mirrors modules/cms/src/fe/queries.ts.
//
// THE PATTERN: each factory takes PLAIN values (never an accessor) and is CALLED
// inside the options function at the screen —
//   const q = useQuery(() => auditQueries.recordTrail("invoice", id(), search()));
// Because the whole options function re-runs when its inputs change, reactivity
// lives at the call site and the factory stays a pure value→queryOptions map.
//
// staleTime tiers: the trail is APPEND-ONLY, so a page never goes stale in the
// "wrong data" sense — only in the "there may be newer rows" sense. 30s on the
// live feeds; a single row is IMMUTABLE once written, so its detail is cached
// long (5m) and never refetched on focus.
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/solid-query";
import { unwrap } from "@erp/api-client";
import type { AuditEventDto, AuditTrailPage, AuditTrailQuery, AuditTrailStats, AuditTrailStatsQuery } from "../schemas/index.js";
import { auditApi } from "./client.js";
import { auditKeys } from "./keys.js";

const STALE = {
  feed: 30_000,
  /** A written trail row never changes (Law A1) — nothing to re-fetch. */
  immutable: 5 * 60_000,
} as const;

export const auditQueries = {
  /**
   * THE feed. Which index serves it is decided by the filters (ADR-004 §7) —
   * ?actor, ?entity+?entityId, ?module, or a bare time range. Auto-scoped
   * server-side for a module-scoped reader.
   */
  feed: (filters: AuditTrailQuery) =>
    queryOptions({
      queryKey: auditKeys.trail.feed(filters),
      queryFn: () => unwrap<AuditTrailPage>(auditApi.trail.$get({ query: filters })),
      staleTime: STALE.feed,
      placeholderData: keepPreviousData,
    }),

  /**
   * THE SAME feed as ONE ACCUMULATING STREAM (2026-09-22, the owner: "now let's
   * do it with every table and card listing of every portal").
   *
   * NOTHING ABOUT THE SERVER CHANGES — same route, same filter projection as
   * `feed` above, same `{data, nextCursor}`; the only difference is that pages
   * ACCUMULATE in one cache entry instead of replacing each other.
   *
   * · `initialPageParam: undefined` — the first page carries no cursor, exactly
   *   as the first cursor-less request always did.
   * · `getNextPageParam` reads the wire's own `nextCursor`; `null` (the last
   *   page) becomes `undefined`, which is how TanStack says "no more".
   * · The filters are the key MINUS the cursor — a cursor is a position inside
   *   the stream, not part of its identity. `"infinite"` on the end keeps this
   *   entry distinct from the paged `feed(filters)` one — which, since the trail
   *   converted, has NO product consumer left (RecordHistory reads
   *   `recordTrail`, a different route). It is kept as a published affordance,
   *   not because something calls it; retiring it is a deliberate decision, not
   *   a side effect of this change.
   * · NO `keepPreviousData`: a filter change starts a NEW stream, and holding
   *   the old events under the new controls would show a list that does not
   *   match them. The table shows its skeleton rows instead.
   */
  feedInfinite: (filters: Omit<AuditTrailQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...auditKeys.trail.feed(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<AuditTrailPage>(auditApi.trail.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: AuditTrailPage) => last.nextCursor ?? undefined,
      staleTime: STALE.feed,
    }),

  /**
   * The console's FIGURES over a window (GET /trail/stats): how many events,
   * refusals, failures and people. `from` is required by the contract — the trail
   * is the one table this module refuses to count without a bound — so the caller
   * always names the window, and it is part of the cache key.
   */
  stats: (window: AuditTrailStatsQuery) =>
    queryOptions({
      queryKey: auditKeys.trail.stats(window),
      queryFn: () => unwrap<AuditTrailStats>(auditApi.trail.stats.$get({ query: window })),
      staleTime: STALE.feed,
    }),

  /**
   * Everything that happened TO one record — what RecordHistory renders. Same
   * route as `feed` (?entity + ?entityId ⇒ audit_event_subject_at_idx), but its
   * OWN cache branch, so a host screen can invalidate one record's history
   * without sweeping every console page.
   */
  recordTrail: (entity: string, entityId: string, filters: Omit<AuditTrailQuery, "entity" | "entityId">) =>
    queryOptions({
      queryKey: auditKeys.trail.record(entity, entityId, filters),
      queryFn: () => unwrap<AuditTrailPage>(auditApi.trail.$get({ query: { ...filters, entity, entityId } })),
      staleTime: STALE.feed,
      placeholderData: keepPreviousData,
    }),

  /** One row. Immutable once written (Law A1), so it is cached long. */
  entry: (id: string) =>
    queryOptions({
      queryKey: auditKeys.trail.detail(id),
      queryFn: () => unwrap<AuditEventDto>(auditApi.trail[":id"].$get({ param: { id } })),
      staleTime: STALE.immutable,
    }),
} as const;

export type AuditQueries = typeof auditQueries;
